import { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { AlertTriangleIcon, CheckCircleIcon, ClockIcon, CreditCardIcon } from "lucide-react";
import ActivityContentWrapper from "./activity-content-wrapper";
import ActivityIconWrapper from "./activity-icon-wrapper";
import { ActivityItemHeader } from "./activity-item-header";
import ActivityWrapper from "./activity-wrapper";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@babackapp/ui/components/accordion";
import useFormatPrice from "@/hooks/use-format-price.ts";
import config from "@/config.ts";

type TransactionRefundedActivityProps = {
  activity: ReturnRequestLogDto;
};

type TransactionRefundedActivityValues = {
  accountId: string;
  amount: string;
  currency: string;
  lang: string;
  last4: string;
  origin: string;
  transactionId: string;
  status?: 'pending' | 'success' | 'failure';
  refundId?: string;
  errorCode?: string | null;
}

function RefundStatusIcon({ status }: { status?: string }) {
  switch (status) {
    case 'pending':
      return (
        <ActivityIconWrapper className="bg-amber-100">
          <ClockIcon className="size-4 text-amber-600" />
        </ActivityIconWrapper>
      );
    case 'success':
      return (
        <ActivityIconWrapper className="bg-green-100">
          <CheckCircleIcon className="size-4 text-green-600" />
        </ActivityIconWrapper>
      );
    case 'failure':
      return (
        <ActivityIconWrapper className="bg-red-100">
          <AlertTriangleIcon className="size-4 text-red-600" />
        </ActivityIconWrapper>
      );
    default:
      return (
        <ActivityIconWrapper>
          <CreditCardIcon className="size-4" />
        </ActivityIconWrapper>
      );
  }
}

export default function TransactionRefundedActivity({
  activity,
}: TransactionRefundedActivityProps) {
  const t = useTranslation();
  const formatPrice = useFormatPrice()
  const transaction = activity.params as unknown as TransactionRefundedActivityValues;

  const testUri = config.isProd ? "" : "test/";
  const externalLink = `https://dashboard.stripe.com/${activity.params?.accountId}/${testUri}payments/${activity.params?.transactionId}`;

  const formattedAmount = formatPrice({
    amount: parseFloat(transaction.amount),
    currency: transaction.currency
  });

  const titleKey = {
    pending: "returnDetail.transaction.refundPendingTitle",
    success: "returnDetail.transaction.refundedTitle",
    failure: "returnDetail.transaction.refundFailedTitle",
  }[transaction.status ?? ''] ?? "returnDetail.transaction.refundedTitle";

  const title = t(titleKey, { amount: formattedAmount });

  return (
    <ActivityWrapper>
      <RefundStatusIcon status={transaction.status} />
      <ActivityContentWrapper>
        <div className="flex items-center gap-2">
          <Accordion type="single" collapsible>
            <AccordionItem value="item-1" className="border-0">
              <AccordionTrigger className="p-0">
                <ActivityItemHeader
                  title={title}
                  timestamp={activity.date}
                >
                </ActivityItemHeader>
              </AccordionTrigger>
              <AccordionContent>
                <div className="text-xs">
                  <div className="mt-1">{t("returnDetail.transaction.cardNumber")}: **** ****
                    **** {transaction.last4 || '****'}</div>
                  <div className="mt-1 mb-1">{t("returnDetail.transaction.cardHolder")}: {transaction.origin}</div>
                  <a
                    className="text-blue-500"
                    href={externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("returnDetail.transaction.externalLink")}
                  </a>
                  {transaction.status === 'failure' && transaction.errorCode && (
                    <div className="mt-2 rounded-md bg-red-50 p-2">
                      <p className="font-medium text-red-700">
                        {t("returnDetail.transaction.refundErrorCode")}: {transaction.errorCode}
                      </p>
                      <p className="mt-1 text-red-600">
                        {t("returnDetail.transaction.refundErrorSuggestion")}
                      </p>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </ActivityContentWrapper>
    </ActivityWrapper>
  );
}
